/* ==========================================================================
   POC CORE JS — Premium Clothing Brand Theme
   ========================================================================== */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     Lenis Smooth Scroll
     -------------------------------------------------------------------------- */
  let lenis;

  function initLenis() {
    if (typeof Lenis === 'undefined') return;
    
    lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          lenis.scrollTo(target, { offset: 0 });
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Custom Cursor
     -------------------------------------------------------------------------- */
  function initCursor() {
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const cursor = document.createElement('div');
    cursor.className = 'poc-cursor';
    document.body.appendChild(cursor);

    let mouseX = 0, mouseY = 0;
    let cursorX = 0, cursorY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      cursor.classList.add('active');
    });

    document.addEventListener('mouseleave', () => {
      cursor.classList.remove('active');
    });

    // Hover states
    const hoverElements = document.querySelectorAll('a, button, [data-cursor-hover]');
    hoverElements.forEach(el => {
      el.addEventListener('mouseenter', () => cursor.classList.add('hover'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('hover'));
    });

    function animateCursor() {
      const ease = 0.15;
      cursorX += (mouseX - cursorX) * ease;
      cursorY += (mouseY - cursorY) * ease;
      cursor.style.transform = `translate(${cursorX}px, ${cursorY}px) translate(-50%, -50%)`;
      requestAnimationFrame(animateCursor);
    }
    animateCursor();
  }

  /* --------------------------------------------------------------------------
     Scroll Reveal
     -------------------------------------------------------------------------- */
  function initReveal() {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    reveals.forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------------------------------
     Countdown Timer
     -------------------------------------------------------------------------- */
  function initCountdown() {
    const countdownEl = document.querySelector('[data-countdown]');
    if (!countdownEl) return;

    const targetDate = new Date(countdownEl.dataset.countdown).getTime();
    
    const daysEl = countdownEl.querySelector('[data-days]');
    const hoursEl = countdownEl.querySelector('[data-hours]');
    const minutesEl = countdownEl.querySelector('[data-minutes]');
    const secondsEl = countdownEl.querySelector('[data-seconds]');

    function pad(num) {
      return num.toString().padStart(2, '0');
    }

    function update() {
      const now = new Date().getTime();
      const diff = targetDate - now;

      if (diff <= 0) {
        daysEl.textContent = '00';
        hoursEl.textContent = '00';
        minutesEl.textContent = '00';
        secondsEl.textContent = '00';
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      daysEl.textContent = pad(days);
      hoursEl.textContent = pad(hours);
      minutesEl.textContent = pad(minutes);
      secondsEl.textContent = pad(seconds);
    }

    update();
    setInterval(update, 1000);
  }

  /* --------------------------------------------------------------------------
     Video Hero
     -------------------------------------------------------------------------- */
  function initVideoHero() {
    const videos = document.querySelectorAll('.poc-hero__video');
    videos.forEach(video => {
      // Ensure video plays
      video.play().catch(() => {
        // Autoplay blocked, show poster
        video.style.display = 'none';
        const poster = video.parentElement.querySelector('.poc-hero__image');
        if (poster) poster.style.display = 'block';
      });
    });
  }

  /* --------------------------------------------------------------------------
     Parallax (simple CSS-based)
     -------------------------------------------------------------------------- */
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (!parallaxElements.length) return;

    function updateParallax() {
      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.5;
        const rect = el.getBoundingClientRect();
        const scrolled = window.scrollY;
        const rate = (rect.top + scrolled - window.innerHeight / 2) * speed * 0.1;
        el.style.transform = `translateY(${rate}px)`;
      });
    }

    if (lenis) {
      lenis.on('scroll', updateParallax);
    } else {
      window.addEventListener('scroll', updateParallax, { passive: true });
    }
    updateParallax();
  }

  /* --------------------------------------------------------------------------
     Loading Screen
     -------------------------------------------------------------------------- */
  function initLoader() {
    const loader = document.querySelector('.poc-loader');
    if (!loader) return;

    // Hide loader after page loads
    window.addEventListener('load', () => {
      setTimeout(() => {
        loader.classList.add('hidden');
        document.body.classList.add('poc-page-loaded');
      }, 800);
    });

    // Fallback: hide after 3 seconds max
    setTimeout(() => {
      loader.classList.add('hidden');
      document.body.classList.add('poc-page-loaded');
    }, 3000);
  }

  /* --------------------------------------------------------------------------
     Header Hide on Scroll
     -------------------------------------------------------------------------- */
  function initHeaderScroll() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;
    const threshold = 100;

    function updateHeader() {
      const currentScroll = window.scrollY;
      
      if (currentScroll <= threshold) {
        header.classList.remove('poc-header-hidden');
        header.classList.add('poc-header-visible');
      } else if (currentScroll > lastScroll && currentScroll > threshold) {
        // Scrolling down
        header.classList.add('poc-header-hidden');
        header.classList.remove('poc-header-visible');
      } else if (currentScroll < lastScroll) {
        // Scrolling up
        header.classList.remove('poc-header-hidden');
        header.classList.add('poc-header-visible');
      }

      lastScroll = currentScroll;
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }

    if (lenis) {
      lenis.on('scroll', onScroll);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* --------------------------------------------------------------------------
     Enhanced Cursor Effects
     -------------------------------------------------------------------------- */
  function initEnhancedCursor() {
    const cursor = document.querySelector('.poc-cursor');
    if (!cursor) return;

    // Media hover
    document.querySelectorAll('.poc-product-card__media, .poc-lookbook__item, .poc-chapter__media').forEach(el => {
      el.addEventListener('mouseenter', () => {
        cursor.classList.add('hover-media');
        cursor.classList.add('hover-view');
      });
      el.addEventListener('mouseleave', () => {
        cursor.classList.remove('hover-media');
        cursor.classList.remove('hover-view');
      });
    });

    // Link hover
    document.querySelectorAll('a:not([data-cursor-hover])').forEach(el => {
      el.addEventListener('mouseenter', () => cursor.classList.add('hover-link'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('hover-link'));
    });
  }

  /* --------------------------------------------------------------------------
     Image Reveal on Scroll
     -------------------------------------------------------------------------- */
  function initImageReveal() {
    const images = document.querySelectorAll('.poc-image-reveal');
    if (!images.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.2
    });

    images.forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------------------------------
     Text Split Animation
     -------------------------------------------------------------------------- */
  function initTextSplit() {
    const elements = document.querySelectorAll('[data-split-text]');
    
    elements.forEach(el => {
      const text = el.textContent;
      const words = text.split(' ');
      
      el.innerHTML = words.map(word => {
        const chars = word.split('').map(char => 
          `<span class="poc-char">${char}</span>`
        ).join('');
        return `<span class="poc-word">${chars}</span>`;
      }).join(' ');
      
      el.classList.add('poc-split-text');
    });
    
    // Observe split text elements
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    document.querySelectorAll('.poc-split-text').forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------------------------------
     Magnetic Buttons
     -------------------------------------------------------------------------- */
  function initMagnetic() {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    
    const magnets = document.querySelectorAll('.poc-magnetic, .poc-btn');
    
    magnets.forEach(magnet => {
      magnet.addEventListener('mousemove', (e) => {
        const rect = magnet.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        magnet.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
      });
      
      magnet.addEventListener('mouseleave', () => {
        magnet.style.transform = 'translate(0, 0)';
      });
    });
  }

  /* --------------------------------------------------------------------------
     Stagger Reveal
     -------------------------------------------------------------------------- */
  function initStagger() {
    const staggers = document.querySelectorAll('[data-stagger]');
    if (!staggers.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    staggers.forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------------------------------
     Smooth Page Transitions
     -------------------------------------------------------------------------- */
  function initPageTransitions() {
    // Add transition class on link click
    document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]').forEach(link => {
      if (link.target === '_blank') return;
      if (link.href.includes('#')) return;
      
      link.addEventListener('click', (e) => {
        const href = link.href;
        e.preventDefault();
        
        document.body.classList.add('poc-page-exiting');
        
        setTimeout(() => {
          window.location.href = href;
        }, 400);
      });
    });
  }

  /* --------------------------------------------------------------------------
     Initialize
     -------------------------------------------------------------------------- */
  function init() {
    // Start page loading state
    document.body.classList.add('poc-page-loading');
    
    initLoader();
    initLenis();
    initCursor();
    initReveal();
    initCountdown();
    initVideoHero();
    
    // Delay these to ensure DOM is ready
    setTimeout(() => {
      initParallax();
      initHeaderScroll();
      initEnhancedCursor();
      initImageReveal();
      initTextSplit();
      initMagnetic();
      initStagger();
      initPageTransitions();
    }, 100);

    // Page loaded
    window.addEventListener('load', () => {
      document.body.classList.remove('poc-page-loading');
      document.body.classList.add('poc-page-loaded');
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.pocTheme = { lenis, initReveal, initCountdown };

})();
